import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import carouselItem from "../assets/images/trading/buy_sell_carousel_item.svg";
import { PlusIcon } from "lucide-react";
import { useFeatureFlagsByCategory } from "../hooks/query/queries";

const WalletCarousel = () => {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");

  const slides = useMemo(() => {
    return [
      {
        id: 0,
        title: "Welcome to your \nDigital Wallet.",
        description:
          "A calmer way to manage your money.\nCrypto, cash, and payments all in one place.",
        image: carouselItem,
      },
      (featureFlags?.deposit || featureFlags?.withdraw) && {
        id: 1,
        title: "Deposit & Withdraw Fiat",
        description:
          "Whether it's jumping on an opportunity or covering life's surprises, your wallet's got you when it's loaded and ready.",
        image: carouselItem,
      },
      (featureFlags?.buyCrypto || featureFlags?.sellCrypto) && {
        id: 2,
        title: "Buy & Sell Crypto.",
        description:
          "Switch between cash and crypto whenever it suits you, to pay, send, or save the way you want.",
        image: carouselItem,
      },
      (featureFlags?.sendCrypto || featureFlags?.receiveCrypto) && {
        id: 3,
        title: "Send & Receive Crypto.",
        description:
          "Send crypto to and from crypto exchanges, wallets, and platforms around the globe. Fast, secure, and on your terms.",
        image: carouselItem,
      },
      featureFlags?.eightPay && {
        id: 4,
        title: "88Pay",
        description:
          "Send cash or crypto to anyone on 80eight.\nInstant and free.",
        image: carouselItem,
      },
      featureFlags?.autostack && {
        id: 5,
        title: "AutoStack",
        description:
          "Whether it's jumping on an opportunity or covering life's surprises, your wallet's got you when it's loaded and ready.",
        image: carouselItem,
      },
    ].filter(Boolean);
  }, [featureFlags]);

  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % slides.length);
      }, 8000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, slides.length]);

  const handleDotClick = (index: number) => {
    if (index >= 0 && index < slides.length) {
      setActiveSlide(index);
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const getCurrentSlide = () => {
    const index =
      activeSlide >= 0 && activeSlide < slides.length ? activeSlide : 0;
    const slide = slides.find((_, i) => i === index);
    if (slide) return slide;
    // slides[0] always exists since slides is a hardcoded array with elements
    return slides[0];
  };
  const currentSlide = getCurrentSlide();

  const handleButtonClick = (buttonText: string) => {
    if (buttonText === "Deposit Cash" && featureFlags?.deposit) {
      navigate("/trading/digitalWallet", {
        state: {
          parentTab: "withdraw-deposit",
          childTab: "deposit",
        },
      });
    } else if (buttonText === "Deposit Crypto" && featureFlags?.receiveCrypto) {
      navigate("/trading/digitalWallet", {
        state: {
          parentTab: "send-receive",
          tab: "receive",
          childTab: "receive",
        },
      });
    }
  };

  const showDepositCash = featureFlags?.deposit;
  const showDepositCrypto = featureFlags?.receiveCrypto;

  return (
    <div
      className="h-[441px] autostack-header-card bg-white rounded-lg p-6 flex flex-col justify-between border border-[1px] border-[#EBE9EF]"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4">
        <div className="flex items-center justify-center">
          <img
            src={currentSlide.image}
            alt={currentSlide.title}
            className="w-[133px] lg:w-[194px]"
          />
        </div>

        <h3 className="text-[24px] font-semibold text-brand-primary whitespace-pre-line !mt-2">
          {currentSlide.title}
        </h3>

        <p className="text-[14px] text-gray-600 leading-relaxed max-w-lg whitespace-pre-line">
          {currentSlide.description}
        </p>
        {(showDepositCash || showDepositCrypto) && (
          <div className="flex flex-col justify-center items-center sm:flex-row gap-4 mt-4 w-full">
            {showDepositCash && (
              <button
                onClick={() => handleButtonClick("Deposit Cash")}
                className="h-[48px] flex items-center justify-center w-full lg:!w-[149px] bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-gray-50 transition-colors"
              >
                Deposit Cash
              </button>
            )}
            {showDepositCrypto && (
              <button
                onClick={() => handleButtonClick("Deposit Crypto")}
                className="h-[48px] flex items-center justify-center w-full lg:!w-[173px] bg-white text-[white] rounded-full !text-[14px] font-semibold btn-primary gap-2 hover:opacity-90 transition-opacity"
              >
                <PlusIcon className="w-3 h-3 md:w-4 md:h-4" />
                Deposit Crypto
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-center space-x-2 mt-4">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => handleDotClick(index)}
            className={`w-2 h-2 rounded-full transition-colors duration-800 ${
              index === activeSlide ? "bg-brand-light w-6" : "bg-gray-300"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default WalletCarousel;
